import { useState } from 'react';
import { Service } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';

export interface BuildingTypeConfig {
  id: number;
  code: string;
  service_id: number;
  nom: string;
  icon: string | null;
  couleur: string | null;
  ordre: number;
  prix: number;
  actif: number;
  created_at?: string;
}

interface UseBuildingTypeConfigParams {
  services: Service[];
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
}

const emptyBuildingForm = {
  code: '',
  nom: '',
  icon: '',
  couleur: 'text-gray-400',
  ordre: 0,
  actif: 1,
  service_id: 1,
  prix: 0,
};

export function useBuildingTypeConfig({
  services,
  setError,
  setSuccess,
}: UseBuildingTypeConfigParams) {
  const [buildingTypesConfig, setBuildingTypesConfig] = useState<BuildingTypeConfig[]>([]);
  const [buildingForm, setBuildingForm] = useState(emptyBuildingForm);
  const [editingBuildingId, setEditingBuildingId] = useState<number | null>(null);
  const [savingBuilding, setSavingBuilding] = useState(false);

  const handleEditBuilding = (b: BuildingTypeConfig) => {
    setEditingBuildingId(b.id);
    setBuildingForm({
      code: b.code,
      nom: b.nom,
      icon: b.icon || '',
      couleur: b.couleur || 'text-gray-400',
      ordre: b.ordre,
      actif: b.actif,
      service_id: b.service_id ?? services[0]?.id ?? 1,
      prix: typeof b.prix === 'number' ? b.prix : Number(b.prix) || 0,
    });
  };

  const handleCancelEditBuilding = () => {
    setEditingBuildingId(null);
    setBuildingForm({
      ...emptyBuildingForm,
      service_id: services[0]?.id ?? 1,
    });
  };

  const handleSaveBuilding = async () => {
    const code = buildingForm.code.trim();
    const nom = buildingForm.nom.trim();
    if (!code || !nom) {
      setError('Code et nom sont requis.');
      return;
    }
    setError(null);
    setSavingBuilding(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        code: code.toLowerCase().replace(/\s+/g, '_'),
        nom,
        icon: buildingForm.icon.trim() || null,
        couleur: buildingForm.couleur.trim() || null,
        ordre: buildingForm.ordre,
        actif: buildingForm.actif ? 1 : 0,
        service_id: buildingForm.service_id,
        prix: buildingForm.prix,
      };
      if (editingBuildingId) {
        const res = await fetch(`${apiUrl}/api/buildings/config/${editingBuildingId}`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setBuildingTypesConfig((prev) => prev.map((x) => (x.id === editingBuildingId ? data.data : x)));
          setSuccess('Type de bâtiment mis à jour.');
          handleCancelEditBuilding();
        } else {
          setError(data.error || 'Erreur lors de la mise à jour.');
        }
      } else {
        const res = await fetch(`${apiUrl}/api/buildings/config`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setBuildingTypesConfig((prev) => [...prev, data.data]);
          setSuccess('Type de bâtiment ajouté.');
          setBuildingForm({
            ...emptyBuildingForm,
            service_id: services[0]?.id ?? 1,
          });
        } else {
          setError(data.error || 'Erreur lors de l\'ajout.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    } finally {
      setSavingBuilding(false);
    }
  };

  const handleDeleteBuilding = async (id: number) => {
    if (!window.confirm('Supprimer ce type de bâtiment ? Les bâtiments déjà placés sur la carte garderont leur type.')) return;
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${apiUrl}/api/buildings/config/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setBuildingTypesConfig((prev) => prev.filter((x) => x.id !== id));
        setSuccess('Type de bâtiment supprimé.');
        if (editingBuildingId === id) handleCancelEditBuilding();
      } else {
        setError(data.error || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    }
  };

  return {
    buildingTypesConfig,
    setBuildingTypesConfig,
    buildingForm,
    setBuildingForm,
    editingBuildingId,
    savingBuilding,
    handleEditBuilding,
    handleCancelEditBuilding,
    handleSaveBuilding,
    handleDeleteBuilding,
  };
}
